"use client";

import React from "react";
import { Activity, SlidersHorizontal as Sliders, ShieldAlert, Cable, ArrowRight, CircleAlert as AlertCircle, FileCheck2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
interface ChecklistItem {
  id: string;
  code: string;
  title: string;
  description: string;
  icon: React.ElementType;
  checkLabel: string;
}
const CHECKLIST_ITEMS: ChecklistItem[] = [{
  id: "item-base",
  code: "SPEC-01 // CORE",
  title: "Bull & Motion Base",
  description: "Confirm that the main machine assembly is included.",
  icon: Activity,
  checkLabel: "Assembly Verification"
}, {
  id: "item-ctrl",
  code: "SPEC-02 // CTRL",
  title: "Operator Controller",
  description: "Check whether the controller shown or described comes with the machine.",
  icon: Sliders,
  checkLabel: "Console Inclusion"
}, {
  id: "item-arena",
  code: "SPEC-03 // SAFE",
  title: "Inflatable Safety Arena & Blower",
  description: "Confirm whether these components are part of the advertised package.",
  icon: ShieldAlert,
  checkLabel: "Perimeter & Air System"
}, {
  id: "item-acc",
  code: "SPEC-04 // ACCS",
  title: "Cables & Accessories",
  description: "Check what cables, remotes, covers, documentation or other accessories are included.",
  icon: Cable,
  checkLabel: "Harness & Docs"
}];
export default function CheckPackage() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="checkincludedpackagecomparison" data-nav="light" className="relative w-full bg-background text-foreground py-20 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Section Header */}
        <div className="space-y-4 max-w-3xl">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="rounded-none border-border bg-muted/50 px-2.5 py-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">
              PACKAGE VERIFICATION SPECIFICATION
            </Badge>
            <span className="hidden sm:inline-block h-px w-12 bg-border" />
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
            Check What Is Included Before You Compare Prices
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Two mechanical bulls with similar prices may represent very different packages. One listing may include a complete setup, while another may require additional components before it can operate as intended.
          </p>
        </div>

        {/* 4-Column Checklist Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CHECKLIST_ITEMS.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full rounded-none border border-border bg-card text-card-foreground p-0 shadow-none hover:border-primary transition-[border-color] duration-200">
                  <div className="p-6 flex flex-col justify-between h-full space-y-6">
                    {/* Top Specification Plate Tag */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-border/80 pb-3">
                        <span className="font-mono text-xs text-muted-foreground tracking-wide">
                          {item.code}
                        </span>
                        <span className="inline-flex items-center justify-center p-1.5 bg-muted text-primary">
                          <IconComponent className="size-4" />
                        </span>
                      </div>

                      <CardHeader className="p-0 gap-2">
                        <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground leading-snug">
                          {item.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="p-0">
                        <p className="text-sm font-sans text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </CardContent>
                    </div>

                    {/* Bottom Status Checklist Strip */}
                    <div className="pt-4 border-t border-dashed border-border/80 flex items-center justify-between font-mono text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <FileCheck2 className="size-3.5 text-primary" />
                        {item.checkLabel}
                      </span>
                      <span className="text-[10px] uppercase bg-muted/60 px-1.5 py-0.5 border border-border">
                        VERIFY
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing Specification Plate Container */}
        <div className="border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-none">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4 max-w-2xl">
              <div className="p-2 bg-primary/10 text-primary shrink-0">
                <AlertCircle className="size-6" />
              </div>
              <div className="space-y-1">
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest block">
                  Mandatory Buyer Protocol
                </span>
                <p className="text-base sm:text-lg font-medium text-foreground leading-normal">
                  If a component is not listed as included, ask before assuming it comes with the purchase.
                </p>
              </div>
            </div>

            <div className="shrink-0 flex items-center">
              <Button asChild variant="outline" className="rounded-none border-border bg-background hover:bg-primary hover:text-primary-foreground hover:border-primary text-foreground font-sans uppercase tracking-wider text-xs font-semibold px-6 py-5 transition-[background-color,color,border-color] duration-200">
                <Link to="/MechanicalBullPriceGuidePage">
                  Review Complete Mechanical Bull Price Guide
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}